import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ApiClientError } from '@riskpay/ui-components';
import { AppShell, type Route } from './components/AppShell';
import { SignIn } from './screens/SignIn';
import { SignUp } from './screens/SignUp';
import { ForgotPassword } from './screens/ForgotPassword';
import { Dashboard } from './screens/Dashboard';
import { Transactions } from './screens/Transactions';
import { Settlements } from './screens/Settlements';
import { SettlementAccounts } from './screens/SettlementAccounts';
import { Disputes } from './screens/Disputes';
import { Payouts } from './screens/Payouts';
import { Developers } from './screens/Developers';
import { Onboarding } from './screens/Onboarding';
import { api, useAuth } from './lib/client';

/**
 * A 401 means the session is already gone — retrying just burns requests and
 * delays the redirect to sign-in. Everything else gets two attempts.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        error instanceof ApiClientError && error.status === 401 ? false : failureCount < 2,
    },
  },
});

/**
 * Shown for the whole session when a staff member has opened this dashboard.
 *
 * It cannot be dismissed, and it names the merchant rather than saying
 * something vague like "impersonation mode". Staff have full access here —
 * including moving money — so the failure this guards against is not
 * misunderstanding the feature, it is forgetting which company's screen you
 * are on after twenty minutes and a phone call.
 */
function StaffBanner() {
  const staff = useAuth((s) => s.session?.staff);
  const signOut = useAuth((s) => s.signOut);
  if (!staff) return null;

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning-ring bg-warning-soft px-4 py-2 text-xs">
      <span className="font-semibold text-warning-text">Viewing as {staff.merchant}</span>
      <span className="text-warning-text/80">
        You are {staff.email} in {staff.actingAs}&apos;s seat. Anything you do here is recorded against your own
        name.
      </span>
      <button
        type="button"
        onClick={signOut}
        className="ml-auto rounded border border-warning-ring px-2 py-0.5 font-medium text-warning-text hover:bg-warning-soft"
      >
        Leave
      </button>
    </div>
  );
}

function Portal() {
  const [route, setRoute] = useState<Route>('dashboard');

  // Pulled once at the shell level: the KYC state drives the sidebar badge and
  // the live-mode banner, so every screen would otherwise refetch it.
  const kyc = useQuery({
    queryKey: ['kyc', 'status'],
    queryFn: () => api.get<{ merchant: { kyc_status: string } }>('/kyc'),
    staleTime: 120_000,
  });

  return (
    <>
      <StaffBanner />
      <AppShell route={route} onNavigate={setRoute} kycStatus={kyc.data?.merchant.kyc_status}>
      {route === 'dashboard' && <Dashboard onViewAll={() => setRoute('transactions')} />}
      {route === 'transactions' && <Transactions />}
      {route === 'settlements' && <Settlements />}
      {route === 'payouts' && <Payouts />}
      {route === 'settlement-accounts' && <SettlementAccounts />}
      {route === 'disputes' && <Disputes />}
      {route === 'developers' && <Developers />}
      {route === 'onboarding' && <Onboarding />}
      </AppShell>
    </>
  );
}

type PublicRoute = 'signin' | 'signup' | 'forgot';

/** Maps the address bar onto the three unauthenticated screens. */
function routeFromPath(pathname: string): PublicRoute {
  if (pathname.startsWith('/signup')) return 'signup';
  if (pathname.startsWith('/forgot-password')) return 'forgot';
  return 'signin';
}

export function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const [route, setRoute] = useState<PublicRoute>(() => routeFromPath(window.location.pathname));

  // Keep the address bar and the rendered screen in step, so the browser's
  // back button behaves and a /signup link from the marketing site lands in
  // the right place.
  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = (to: PublicRoute) => {
    const path = to === 'signup' ? '/signup' : to === 'forgot' ? '/forgot-password' : '/';
    window.history.pushState({}, '', path);
    setRoute(to);
  };

  return (
    <QueryClientProvider client={queryClient}>
      {isAuthenticated ? (
        <Portal />
      ) : route === 'signup' ? (
        <SignUp onNavigate={go} />
      ) : route === 'forgot' ? (
        <ForgotPassword onNavigate={go} />
      ) : (
        <SignIn onNavigate={go} />
      )}
    </QueryClientProvider>
  );
}
